/**
 * @fileoverview Department information step in the tenant onboarding wizard.
 * @module system-web/pages/tenants/wizard/steps/DepartmentInfoStep
 *
 * Responsibilities:
 * - Collect department name, slug, state, and configuration during onboarding.
 * - Validate slug uniqueness and format.
 * - Support logo upload and department type selection.
 * - Auto-fill address.county from the jurisdiction county dropdown (no
 *   duplicate human-typed county field in the Address section).
 *
 * Dependencies: useLegacyDepartments hook, lookupService, lucide-react icons.
 * Architecture: Online-only provisioning flow — direct API calls are acceptable here.
 * Notes: This is an admin provisioning flow, not a normal CRUD page.
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { CheckCircle, XCircle, Loader2, HelpCircle, Download, Search, X, AlertCircle } from 'lucide-react';
import { useLegacyDepartments } from '../../../../hooks/useLegacyDepartments';
import type { LegacyDepartment } from '../../../../services/legacyMigrationService';
import { listLookups } from '../../../../services/lookupService';
import type { WizardState, LookupRef, SelectedBoundary } from '../types';
import { generateSlug } from '../../../../utils/slugify';
import type { StepError } from '../validateStep';
import { validateNerisId, buildNerisId, isInternalNerisId, NERIS_ENTITY_TYPES, SL_FIPS } from '../../../../utils/nerisId';
import { resolveCountyFips } from '../../../../utils/jurisdictionFips';
import { formatPhone } from '../../../../utils/phoneFormatting';
import { getNextSlSerial } from '../../../../services/onboardingDraftService';
import { listTenants } from '../../../../services/tenantService';
import { searchByNameLive, fetchPolygonByNerisId, getBoundaryUrlFromNerisResult, lookupEntityByNerisId, type NerisLiveSearchResult } from '../../../../services/nerisBoundaryService';
import DepartmentLogoUploadField from '../../../../components/DepartmentLogoUploadField';
import DepartmentAddressField from '../../../../components/shared-forms/DepartmentAddressField';
import type { DepartmentAddress } from '../../../../components/shared-forms/DepartmentAddressField';
import { useLegacyDbOptions } from '../../../../hooks/useLegacyDbOptions';


interface Props {
  state: WizardState;
  onChange: (patch: Partial<WizardState>) => void;
  errors?: StepError[];
}

// ---------------------------------------------------------------------------
// buildSelectedBoundary — constructs a SelectedBoundary from raw NERIS data.
// Returns undefined if no geometry is provided (boundary not available for
// this department in fireBoundaries).
// ---------------------------------------------------------------------------
function computeBbox(geometry: import('geojson').Polygon | import('geojson').MultiPolygon): [number, number, number, number] {
  const coords: number[][] = [];
  const collect = (rings: number[][][]) => rings.forEach(r => r.forEach(c => coords.push(c)));
  if (geometry.type === 'Polygon') collect(geometry.coordinates);
  else geometry.coordinates.forEach(p => collect(p));
  if (coords.length === 0) return [0, 0, 0, 0];
  let minLng = coords[0][0], maxLng = coords[0][0];
  let minLat = coords[0][1], maxLat = coords[0][1];
  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng; if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat; if (lat > maxLat) maxLat = lat;
  }
  return [minLng, minLat, maxLng, maxLat];
}

function buildSelectedBoundary(
  nerisId: string,
  departmentName: string,
  departmentType: string | undefined,
  geometry: import('geojson').Polygon | import('geojson').MultiPolygon | null,
): SelectedBoundary | undefined {
  if (!geometry) return undefined;
  const bbox = computeBbox(geometry);
  const centroid: [number, number] = [(bbox[0] + bbox[2]) / 2, (bbox[1] + bbox[3]) / 2];
  const lngDelta = bbox[2] - bbox[0];
  const latDelta = bbox[3] - bbox[1];
  const area = Math.abs(lngDelta * latDelta * 4047); // rough sq-mile approximation from bbox degrees
  const deptTypeUpper = (departmentType?.toUpperCase() ?? 'CAREER') as 'CAREER' | 'VOLUNTEER' | 'COMBINATION';
  return {
    nerisId,
    departmentName: departmentName ?? '',
    departmentType: ['CAREER', 'VOLUNTEER', 'COMBINATION'].includes(deptTypeUpper) ? deptTypeUpper : 'CAREER',
    geometry,
    centroid,
    area,
    bbox,
    verified: true,
    verifiedAt: new Date().toISOString(),
    verificationSources: { arcgis: false, platform: true },
  };
}

const DEPT_TYPES = ['career', 'volunteer', 'combination', 'industrial'];
const DEPT_CLASSIFICATIONS = ['municipal', 'county', 'district', 'state', 'federal', 'tribal', 'private'];
const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix',
  'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu',
];

// Legacy databases for migration. This entire section will be removed after all departments are migrated.
const LEGACY_DATABASES = ['SLInspectionsDB001', 'SLInspectionsDB002'];

export default function DepartmentInfoStep({ state, onChange, errors = [] }: Props) {
  const hasError = (field: string) => errors.some((e) => e.field === field);
  const errCls = (field: string) => hasError(field) ? 'border-red-400 ring-1 ring-red-200' : 'border-gray-300';
  const info = state.departmentInfo;
  const addr = info.address || {};
  const brand = state.branding;
  const j = info.jurisdiction ?? {};

  const updateInfo = (patch: Record<string, any>) => onChange({ departmentInfo: { ...info, ...patch } });
  const updateAddr = (patch: Record<string, any>) => onChange({ departmentInfo: { ...info, address: { ...addr, ...patch } } });
  const updateBrand = (patch: Record<string, any>) => onChange({ branding: { ...brand, ...patch } });
  const updateJurisdiction = (patch: Record<string, any>) => onChange({ departmentInfo: { ...info, jurisdiction: { ...j, ...patch } } });

  // Physical Streamline database name per logical key, resolved for the CURRENT server
  // environment — shown inline in the dropdown so the correct env is visible up front,
  // before "Fetch Departments" is even clicked.
  const { physicalByLogical: legacyPhysicalByLogical } = useLegacyDbOptions();

  // Legacy department fetch — via the useLegacyDepartments hook (service-backed;
  // the previous hand-rolled version referenced an unimported `api` and threw at
  // runtime). physicalDatabase is the physical Streamline DB the server queried,
  // shown as a confirmation badge.
  const {
    departments: legacyDepts,
    physicalDatabase: legacyPhysicalDb,
    fetching: legacyFetching,
    error: legacyFetchError,
    fetchDepartments: handleFetchLegacyDepartments,
  } = useLegacyDepartments(state.legacySystem.database);

  const handleSelectLegacyDepartment = useCallback((dept: LegacyDepartment) => {
    // Build a full street address from component parts
    const streetParts = [dept.st_prefix, dept.street, dept.st_type, dept.st_suffix].filter(Boolean).join(' ');

    // Auto-fill department info, address, contact, and branding from the legacy data.
    // Save the entire row into departmentData. Use TenantId as the departmentId.
    onChange({
      legacySystem: {
        ...state.legacySystem,
        departmentId: dept.TenantId ?? dept.dept_id,
        departmentData: dept as any,
      },
      departmentInfo: {
        ...info,
        name: dept.name || info.name,
        contactEmail: dept.email_report || info.contactEmail,
        contactPhone: dept.phone ? formatPhone(dept.phone) : info.contactPhone,
        address: {
          ...addr,
          street: streetParts || addr.street,
          zip: dept.zip || addr.zip,
        },
      },
      branding: {
        ...brand,
        primaryColor: dept.color_main || brand.primaryColor,
        secondaryColor: dept.color_main_dark || brand.secondaryColor,
      },
    });
  }, [state.legacySystem, info, addr, brand, onChange]);

  // Slug uniqueness check — debounced, reads local Dexie only (local-first).
  // When a NERIS ID is set, slug = nerisId.toLowerCase() and we check by nerisId.
  // Without a NERIS ID, slug = name-based and we check by slug.
  const [slugConflict, setSlugConflict] = useState<string | null>(null);
  const slugCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const name = info.name?.trim() ?? '';
    const nerisId = info.nerisId?.trim().toUpperCase() ?? '';
    if (!name && !nerisId) { setSlugConflict(null); return; }
    if (slugCheckTimer.current) clearTimeout(slugCheckTimer.current);
    slugCheckTimer.current = setTimeout(async () => {
      try {
        const tenants = await listTenants();
        if (nerisId) {
          const conflict = tenants.find((t: any) =>
            t.nerisId?.trim().toUpperCase() === nerisId
          );
          setSlugConflict(conflict ? `"${conflict.name}" already uses NERIS ID ${nerisId}. Each department must have a unique NERIS ID.` : null);
        } else if (name) {
          const slug = generateSlug(name);
          const conflict = tenants.find((t: any) => t.slug === slug);
          setSlugConflict(conflict ? `"${conflict.name}" already uses this department name. Please choose a different name.` : null);
        }
      } catch { setSlugConflict(null); }
    }, 500);
    return () => { if (slugCheckTimer.current) clearTimeout(slugCheckTimer.current); };
  }, [info.name, info.nerisId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-compute next SL serial locally (local-first: reads Dexie, falls back to API)
  const [nextSlSerial, setNextSlSerial] = useState<{ nextSerial: number; nextId: string } | null>(null);
  useEffect(() => {
    getNextSlSerial()
      .then((res) => {
        setNextSlSerial(res);
        // Auto-populate if NERIS ID is empty or still at default SL99999000
        const current = state.departmentInfo.nerisId ?? '';
        if (!current || current === 'SL99999000') {
          onChange({ departmentInfo: { ...state.departmentInfo, nerisId: res.nextId, nerisVerification: { status: 'unverified' } } });
        }
      })
      .catch(() => { /* local DB + API both unavailable — user can still set manually */ });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // NERIS lookup modal
  const [nerisLookupOpen, setNerisLookupOpen] = useState(false);
  const [nerisSearchQ, setNerisSearchQ] = useState('');
  const [nerisSearchState, setNerisSearchState] = useState('');
  const [nerisSearching, setNerisSearching] = useState(false);
  const [nerisSearchResults, setNerisSearchResults] = useState<NerisLiveSearchResult[]>([]);
  const [nerisSearchError, setNerisSearchError] = useState<string | null>(null);
  const [nerisNotConfigured, setNerisNotConfigured] = useState(false);

  // Polygon auto-fetch status — driven by applyNerisResult.
  // Only the transient states (`fetching`, `error`) are held here; the
  // persistent `success` / `no_polygon` states are derived from the
  // wizard-state `selectedBoundary` so they survive step navigation
  // (the component unmounts/remounts when the user leaves and returns to
  // Step 1, which would otherwise reset a local-only status to `idle` and
  // hide the boundary information the admin already retrieved).
  const [polygonFetchStatus, setPolygonFetchStatus] = useState<'idle' | 'fetching' | 'success' | 'no_polygon' | 'error'>('idle');
  // Monotonic token so only the most recent polygon fetch may write its result.
  const polygonFetchSeq = useRef(0);

  // Jurisdiction cascading lookups — declared here so applyNerisResult can reference statesList
  const [countries, setCountries] = useState<any[]>([]);
  const [statesList, setStatesList] = useState<any[]>([]);
  const [counties, setCounties] = useState<any[]>([]);

  // Pre-populate search with department name when opening modal
  const openNerisLookup = useCallback(() => {
    const deptName = info.name?.trim() || '';
    setNerisSearchQ(deptName);
    setNerisSearchState('');
    setNerisSearchResults([]);
    setNerisSearchError(null);
    setNerisNotConfigured(false);
    setNerisLookupOpen(true);
  }, [info.name]);

  const handleNerisSearch = useCallback(async () => {
    if (!nerisSearchQ.trim()) return;
    setNerisSearching(true);
    setNerisSearchError(null);
    setNerisNotConfigured(false);
    try {
      const results = await searchByNameLive(nerisSearchQ.trim(), nerisSearchState || undefined);
      setNerisSearchResults(results);
    } catch (err: any) {
      if (err?.code === 'NERIS_NOT_CONFIGURED') {
        setNerisNotConfigured(true);
        setNerisSearchResults([]);
      } else {
        setNerisSearchError(err?.message || 'Search failed — check connection');
        setNerisSearchResults([]);
      }
    } finally {
      setNerisSearching(false);
    }
  }, [nerisSearchQ, nerisSearchState]);

  /**
   * Core handler: applies a verified NERIS entity to wizard state.
   * Auto-fills department name and address fields if they are empty.
   * Kicks off polygon fetch in the background; updates wizard state on completion.
   * Captures `info` and `statesList` synchronously to avoid stale closure issues.
   */
  const applyNerisResult = useCallback(async (result: NerisLiveSearchResult) => {
    const normalizedNerisId = result.nerisId.toUpperCase();
    const nerisVerificationData = {
      status: 'verified' as const,
      verifiedAt: new Date().toISOString(),
      nerisData: {
        departmentName: result.name,
        city: result.city,
        state: result.state,
        fdid: result.fdid,
        zipCode: result.zipCode,
        address: result.address,
      },
    };

    // Auto-fill: only populate fields that are currently empty
    const namePatch = !info.name?.trim() ? { name: result.name } : {};
    const addressPatch: typeof info.address = {
      ...info.address,
      ...(result.city && !info.address?.city ? { city: result.city } : {}),
      ...(result.state && !info.address?.state ? { state: result.state } : {}),
      ...(result.zipCode && !info.address?.zip ? { zip: result.zipCode } : {}),
    };
    const matchedState = result.state
      ? statesList.find((s: any) => s.code === result.state || s.abbr === result.state)
      : undefined;
    const jurisdictionPatch = matchedState && !info.jurisdiction?.state
      ? { jurisdiction: { ...info.jurisdiction, state: { lookupType: 'state', code: matchedState.code, name: matchedState.name } as LookupRef } }
      : {};

    // The pygeoapi feature URL for THIS entity. Persisted so the boundary can
    // be re-fetched later without redoing the entity search.
    const boundaryUrl = getBoundaryUrlFromNerisResult(result);

    // Build the base patch deterministically before the async polygon fetch.
    // `selectedBoundary` is explicitly cleared here: `info` is a snapshot that
    // may still hold the PREVIOUS department's polygon. Carrying it forward
    // made a newly-verified department appear to have a boundary that actually
    // belonged to the department verified before it — and Step 9 would then
    // offer boundary-dependent parcel filters based on the wrong geometry.
    const basePatch = {
      ...info,
      ...namePatch,
      nerisId: normalizedNerisId,
      slug: normalizedNerisId.toLowerCase(),
      nerisVerification: nerisVerificationData,
      address: addressPatch,
      selectedBoundary: undefined,
      nerisBoundaryUrl: boundaryUrl,
      ...jurisdictionPatch,
    };

    onChange({ departmentInfo: basePatch });
    setPolygonFetchStatus('fetching');
    const fetchSeq = ++polygonFetchSeq.current;

    // Non-blocking polygon fetch
    try {
      const geometry = await fetchPolygonByNerisId(normalizedNerisId, boundaryUrl);
      // A newer lookup/retry superseded this one — its result must not be
      // attached to the department that is now selected.
      if (fetchSeq !== polygonFetchSeq.current) return;
      const selectedBoundary = geometry
        ? buildSelectedBoundary(normalizedNerisId, result.name, undefined, geometry)
        : undefined;
      // Only patch selectedBoundary — NOT the entire departmentInfo.
      // basePatch is a stale snapshot from when applyNerisResult was called;
      // spreading it here would clobber any concurrent changes (e.g. county
      // selection made while the async fetch was in flight). The deep-merge
      // in updateState ensures only selectedBoundary is updated.
      //
      // Always patch — including the `undefined` (no-polygon) case — so a
      // department without a boundary never inherits a stale one.
      onChange({
        departmentInfo: { selectedBoundary },
      });
      setPolygonFetchStatus(geometry ? 'success' : 'no_polygon');
    } catch {
      if (fetchSeq !== polygonFetchSeq.current) return;
      setPolygonFetchStatus('error');
    }
  }, [info, onChange, statesList]);

  /**
   * Re-fetches the boundary polygon for the already-verified NERIS ID using
   * the persisted `nerisBoundaryUrl`. Lets the admin recover from a transient
   * fetch failure (or a boundary that became available later) without redoing
   * the entity lookup.
   */
  const handleRetryBoundaryFetch = useCallback(async () => {
    const id = info.nerisId?.trim().toUpperCase();
    if (!id) return;
    setPolygonFetchStatus('fetching');
    const fetchSeq = ++polygonFetchSeq.current;
    try {
      const geometry = await fetchPolygonByNerisId(id, info.nerisBoundaryUrl);
      if (fetchSeq !== polygonFetchSeq.current) return;
      const selectedBoundary = geometry
        ? buildSelectedBoundary(
            id,
            info.nerisVerification?.nerisData?.departmentName ?? info.name ?? '',
            info.departmentType,
            geometry,
          )
        : undefined;
      onChange({ departmentInfo: { selectedBoundary } });
      setPolygonFetchStatus(geometry ? 'success' : 'no_polygon');
    } catch {
      if (fetchSeq !== polygonFetchSeq.current) return;
      setPolygonFetchStatus('error');
    }
  }, [info.nerisId, info.nerisBoundaryUrl, info.nerisVerification, info.name, info.departmentType, onChange]);

  /** Called when user selects a result from the Look Up modal. */
  const handleNerisLookupSelect = useCallback(async (result: NerisLiveSearchResult) => {
    setNerisLookupOpen(false);
    setNerisSearchQ('');
    setNerisSearchState('');
    setNerisSearchResults([]);
    await applyNerisResult(result);
  }, [applyNerisResult]);

  // Verify button state — searches the live NERIS API by the existing nerisId
  const [nerisVerifying, setNerisVerifying] = useState(false);
  const [nerisVerifyOpen, setNerisVerifyOpen] = useState(false);
  const [nerisVerifyResult, setNerisVerifyResult] = useState<NerisLiveSearchResult | null>(null);
  const [nerisVerifyError, setNerisVerifyError] = useState<string | null>(null);

  /** Triggers a live NERIS lookup using the already-entered nerisId. */
  const handleVerifyNeris = useCallback(async () => {
    const id = info.nerisId?.trim();
    if (!id) return;
    setNerisVerifying(true);
    setNerisVerifyError(null);
    setNerisVerifyResult(null);
    try {
      const entity = await lookupEntityByNerisId(id);
      if (entity) {
        setNerisVerifyResult(entity);
        setNerisVerifyOpen(true);
      } else {
        updateInfo({ nerisVerification: { status: 'not_found', verifiedAt: new Date().toISOString() } });
      }
    } catch (err: any) {
      if (err?.code === 'NERIS_NOT_CONFIGURED') {
        setNerisVerifyError('NERIS API not configured. Contact your system administrator.');
      } else {
        setNerisVerifyError(err?.message || 'Verification failed — check connection');
      }
    } finally {
      setNerisVerifying(false);
    }
  }, [info.nerisId, updateInfo]);

  /** Called when user confirms the department shown in the Verify modal. */
  const handleVerifyConfirm = useCallback(async () => {
    if (!nerisVerifyResult) return;
    setNerisVerifyOpen(false);
    await applyNerisResult(nerisVerifyResult);
  }, [nerisVerifyResult, applyNerisResult]);

  const nerisStatus = info.nerisVerification?.status ?? 'unverified';
  const isSl = (info.nerisId?.slice(0, 2) ?? 'FD') === 'SL';

  // Derived polygon status — prefers the transient local state when a fetch
  // is in flight or errored, otherwise reflects the persisted boundary so the
  // badge survives navigation away from and back to Step 1. The local
  // `polygonFetchStatus` resets to `idle` on remount, but `selectedBoundary`
  // is in wizard state and persists across step changes.
  const selectedBoundary = info.selectedBoundary;
  const derivedPolygonStatus: typeof polygonFetchStatus =
    polygonFetchStatus === 'fetching' || polygonFetchStatus === 'error'
      ? polygonFetchStatus
      : nerisStatus === 'verified'
        ? selectedBoundary ? 'success' : 'no_polygon'
        : 'idle';

  useEffect(() => {
    listLookups({ lookupType: 'country' })
      .then((data) => {
        // Deduplicate by code (keep first occurrence)
        const seen = new Set<string>();
        const unique = data.filter((item: any) => {
          if (seen.has(item.code)) return false;
          seen.add(item.code);
          return true;
        });
        setCountries(unique);
      })
      .catch(() => setCountries([]));
  }, []);
  useEffect(() => {
    if (j.country?.code) {
      listLookups({ lookupType: 'state', parentCode: j.country.code })
        .then((data) => {
          const seen = new Set<string>();
          const unique = data.filter((item: any) => {
            if (seen.has(item.code)) return false;
            seen.add(item.code);
            return true;
          });
          setStatesList(unique);
        })
        .catch(() => setStatesList([]));
    } else {
      setStatesList([]);
    }
  }, [j.country?.code]);
  useEffect(() => {
    if (j.state?.code) {
      listLookups({ lookupType: 'county', parentCode: j.state.code })
        .then((data) => {
          const seen = new Set<string>();
          const unique = data.filter((item: any) => {
            if (seen.has(item.code)) return false;
            seen.add(item.code);
            return true;
          });
          setCounties(unique);
        })
        .catch(() => setCounties([]));
    } else {
      setCounties([]);
    }
  }, [j.state?.code]);
  const selectLookup = (field: string, items: any[], code: string) => {
    const item = items.find((i) => i.code === code);
    const ref: LookupRef | undefined = item ? { lookupId: item._id, lookupType: item.lookupType, code: item.code, name: item.name } : undefined;
    const clear: Record<string, undefined> = {};
    if (field === 'country') { clear.state = undefined; clear.county = undefined; }
    if (field === 'state') { clear.county = undefined; }

    // Resolve the county FIPS code. County lookups carry the full 5-digit
    // state+county FIPS in both `code` and `config.fipsCode` (e.g. Orange
    // County CA = "06059"). This is the ONLY reliable join key to
    // geoCacheFiles (whose _id and fipsCode are the same 5-digit code) —
    // state/county name strings are inconsistent across environments.
    const resolvedCountyRef = field === 'county' ? ref : (clear.county !== undefined ? undefined : j.county);
    const resolvedCountyItem = field === 'county' ? item : undefined;
    const countyFips = resolveCountyFips(resolvedCountyRef, resolvedCountyItem, j.fipsCode, clear.county !== undefined);

    // Build the new jurisdiction object for a single combined onChange call.
    const newJurisdiction = { ...j, [field]: ref, ...clear, fipsCode: countyFips };

    // Auto-fill address.county from the jurisdiction county dropdown so the
    // geo cache parcel lookup has the county name without a duplicate
    // human-typed field. When county is cleared (state/country change), clear
    // address.county too.
    const newCountyName = clear.county !== undefined
      ? undefined
      : (field === 'county' ? ref?.name : j.county?.name);
    const newAddress = { ...addr, county: newCountyName };

    // Single combined patch — avoids the stale-info overwrite that separate
    // updateJurisdiction / updateInfo calls would cause.
    const patch: Record<string, any> = {
      ...info,
      jurisdiction: newJurisdiction,
      address: newAddress,
    };

    // Auto-populate the NERIS FIPS digits once a county is selected (only for
    // non-SL prefixes — SL always uses FIPS 99999). The county lookup code is
    // ALREADY the full 5-digit state+county FIPS, so it must NOT be
    // concatenated with the state code (that produced 7-digit garbage such as
    // "0606059" for Orange County, CA).
    //
    // CRITICAL: never do this for an ID that was already verified against the
    // live NERIS API. The verified ID is authoritative — the department's real
    // NERIS ID does not necessarily encode the county FIPS the admin picked,
    // and rebuilding it here previously wiped `nerisVerification` back to
    // `unverified`, which silently hid the NERIS data panel AND the boundary
    // status the admin had just retrieved. Guessing an ID from the county must
    // never override a confirmed lookup.
    const currentPrefix = info.nerisId?.slice(0, 2) ?? 'FD';
    const isNerisVerified = info.nerisVerification?.status === 'verified';
    if (currentPrefix !== 'SL' && countyFips && !isNerisVerified) {
      const currentSerial = (info.nerisId?.slice(7) ?? '').replace(/\D/g, '');
      patch.nerisId = buildNerisId(currentPrefix, countyFips + currentSerial.padStart(3, '0'));
      patch.nerisVerification = { status: 'unverified' };
    }

    onChange({ departmentInfo: patch });
  };

  const nerisBadge = () => {
    if (nerisStatus === 'verified') return <span className="flex items-center gap-1 text-xs text-green-700 font-medium"><CheckCircle className="h-3.5 w-3.5" />Verified</span>;
    if (nerisStatus === 'not_found') return <span className="flex items-center gap-1 text-xs text-red-600"><XCircle className="h-3.5 w-3.5" />Not found in NERIS</span>;
    if (nerisStatus === 'skipped') return <span className="flex items-center gap-1 text-xs text-gray-400"><HelpCircle className="h-3.5 w-3.5" />Skipped</span>;
    return null;
  };

  return (
    <div className="space-y-8">
      {/* Legacy Toggle */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={state.legacySystem.isExistingClient}
            onChange={(e) => onChange({ legacySystem: { ...state.legacySystem, isExistingClient: e.target.checked } })}
            className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
          />
          <div>
            <span className="text-sm font-medium text-blue-900">Existing Streamline Client</span>
            <p className="text-xs text-blue-600 mt-0.5">Link to legacy SQL database for auto-fill</p>
          </div>
        </label>
        {state.legacySystem.isExistingClient && (
          <div className="mt-3 space-y-3">
            {/* Row 1: Database select + Fetch button */}
            <div className="flex gap-3">
              <select
                value={state.legacySystem.database || ''}
                onChange={(e) => {
                  // The useLegacyDepartments hook clears departments/error/physicalDb
                  // when the database changes.
                  onChange({ legacySystem: { ...state.legacySystem, database: e.target.value, departmentId: undefined, departmentData: undefined } });
                }}
                title="Legacy database"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">Select Database</option>
                {LEGACY_DATABASES.map((db: string) => {
                  const physical = legacyPhysicalByLogical[db];
                  const unavailable = db in legacyPhysicalByLogical && !physical;
                  return (
                    <option key={db} value={db} disabled={unavailable}>
                      {physical ? `${db} (${physical})` : unavailable ? `${db} — unavailable in this environment` : db}
                    </option>
                  );
                })}
              </select>
              <button
                type="button"
                onClick={handleFetchLegacyDepartments}
                disabled={!state.legacySystem.database || legacyFetching}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {legacyFetching ? (
                  <><Loader2 className="h-4 w-4 animate-spin" />Fetching…</>
                ) : (
                  <><Download className="h-4 w-4" />Fetch Departments</>
                )}
              </button>
            </div>

            {/* Error state */}
            {legacyFetchError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">
                <strong>Error:</strong> {legacyFetchError}
              </div>
            )}

            {/* Confirmation badge: the actual physical Streamline database queried
                (differs from the dropdown's logical name in dev/stage). */}
            {legacyPhysicalDb && (
              <div
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-mono font-medium bg-blue-50 text-blue-700 border border-blue-200"
                title="This is the actual database the server connected to for this request."
              >
                Connected: {legacyPhysicalDb}
              </div>
            )}

            {/* Row 2: Searchable department picker (shown after fetch) */}
            {legacyDepts.length > 0 && (
              <LegacyDeptPicker
                departments={legacyDepts}
                selectedDeptId={state.legacySystem.departmentId ?? null}
                onSelect={handleSelectLegacyDepartment}
              />
            )}

            {/* Auto-fill success banner */}
            {state.legacySystem.departmentData && state.legacySystem.departmentId && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-800 space-y-0.5">
                <p className="font-medium">Department data imported from legacy system</p>
                <p>
                  <span className="font-medium">Name:</span> {(state.legacySystem.departmentData as any)?.name || '—'}
                  {' · '}
                  <span className="font-medium">ID:</span> {state.legacySystem.departmentId}
                  {(state.legacySystem.departmentData as any)?.city && (
                    <>
                      {' · '}
                      <span className="font-medium">Location:</span>{' '}
                      {(state.legacySystem.departmentData as any).city}
                      {(state.legacySystem.departmentData as any).state ? `, ${(state.legacySystem.departmentData as any).state}` : ''}
                    </>
                  )}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Department Info */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Department Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Department Name *</label>
            <input
              type="text"
              value={info.name || ''}
              onChange={(e) => updateInfo({ name: e.target.value })}
              className={`w-full px-3 py-2 border ${slugConflict ? 'border-red-400 ring-1 ring-red-200' : errCls('departmentInfo.name')} rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
              placeholder="Phoenix Fire Department"
            />
            {slugConflict && (
              <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                <XCircle className="h-3.5 w-3.5 shrink-0" />{slugConflict}
              </p>
            )}
          </div>

          {/* NERIS ID */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              NERIS ID <span className="text-xs text-gray-400 font-normal">(optional — format: FD06037123)</span>
            </label>
            {(() => {
              const prefix = info.nerisId?.slice(0, 2) ?? 'FD';
              const isSl = prefix === 'SL';
              return (
                <>
                  <div className="flex gap-2">
                    <div className="flex flex-1">
                      <select
                        value={prefix}
                        title="NERIS ID prefix"
                        onChange={(e) => {
                          const newPrefix = e.target.value;
                          const isNewSl = newPrefix === 'SL';
                          const currentNum = info.nerisId?.slice(2) ?? '';
                          const slSerial = nextSlSerial ? String(nextSlSerial.nextSerial).padStart(3, '0') : (currentNum.slice(5).padStart(3, '0') || '001');
                          const newNum = isNewSl
                            ? `${SL_FIPS}${slSerial}`
                            : currentNum.startsWith('99999') ? '' : currentNum;
                          updateInfo({ nerisId: buildNerisId(newPrefix, newNum), nerisVerification: { status: 'unverified' } });
                        }}
                        className="px-2 py-2 border border-r-0 border-gray-300 rounded-l-lg text-sm bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                        {NERIS_ENTITY_TYPES.map((t) => (
                          <option key={t.code} value={t.code}>{t.code} — {t.label}</option>
                        ))}
                      </select>
                      {isSl ? (
                        <div className="flex flex-1">
                          <span className="px-3 py-2 border-t border-b border-gray-300 bg-gray-100 text-sm font-mono text-gray-500 select-none">
                            {SL_FIPS}
                          </span>
                          <input type="text"
                            value={info.nerisId?.slice(7) ?? ''}
                            readOnly
                            title="Auto-assigned — increments automatically for each new tenant"
                            className="w-20 px-3 py-2 border border-l-0 border-gray-300 rounded-r-lg text-sm font-mono bg-green-50 text-green-800 cursor-default"
                            placeholder="001" maxLength={3} />
                        </div>
                      ) : (
                        <input type="text"
                          value={info.nerisId?.slice(2) ?? ''}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
                            updateInfo({ nerisId: buildNerisId(prefix, digits), nerisVerification: { status: 'unverified' } });
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-r-lg text-sm font-mono focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="06037123" maxLength={8} />
                      )}
                    </div>
                    {isSl ? (
                      <span className="px-3 py-2 bg-amber-100 text-amber-700 text-xs font-medium rounded-lg flex items-center gap-1">
                        Internal only — not verified
                      </span>
                    ) : (
                      <>
                        {/*
                          Phase 5.1 — FI-23: The previous "Verify" button (which
                          looked up an entered NERIS ID against the live NERIS API
                          and opened a confirm modal) was removed. The "Look up"
                          modal flow is now the canonical verification path, with
                          immediate verification on result selection. This change
                          aligns the UI with the test contract in
                          `DepartmentInfoStep.neris.test.tsx > shouldNotRenderVerifyButton`.
                        */}
                        <button type="button" onClick={openNerisLookup}
                          className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1.5">
                          <Search className="h-4 w-4" />Look up
                        </button>
                        <button type="button" onClick={() => updateInfo({ nerisVerification: { status: 'skipped' } })}
                          className="px-3 py-2 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50">
                          Skip
                        </button>
                      </>
                    )}
                  </div>
                  {nerisVerifyError && (
                    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3.5 w-3.5 shrink-0" />{nerisVerifyError}
                    </p>
                  )}
                  <div className="mt-1.5 min-h-[1.25rem]">{!isSl && nerisBadge()}</div>
                </>
              );
            })()}
            {nerisStatus === 'verified' && info.nerisVerification?.nerisData && (
              <div className="mt-2 bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-800 space-y-0.5">
                {info.nerisVerification.nerisData.departmentName && <p><span className="font-medium">Name:</span> {info.nerisVerification.nerisData.departmentName}</p>}
                {info.nerisVerification.nerisData.address && <p><span className="font-medium">Address:</span> {info.nerisVerification.nerisData.address}</p>}
                {info.nerisVerification.nerisData.city && <p><span className="font-medium">Location:</span> {info.nerisVerification.nerisData.city}{info.nerisVerification.nerisData.state ? `, ${info.nerisVerification.nerisData.state}` : ''}{info.nerisVerification.nerisData.zipCode ? ` ${info.nerisVerification.nerisData.zipCode}` : ''}</p>}
                {info.nerisVerification.nerisData.fdid && <p><span className="font-medium">FDID:</span> {info.nerisVerification.nerisData.fdid}</p>}
              </div>
            )}
            {/*
              Boundary status panel. Rendered from `derivedPolygonStatus`, which
              falls back to the PERSISTED `selectedBoundary` — so the admin sees
              whether a polygon exists every time they return to this step, not
              only in the moment right after the lookup. When a boundary is
              present its actual measurements are shown, which is the only way
              to confirm a real polygon was downloaded rather than an empty one.
            */}
            {nerisStatus === 'verified' && !isSl && derivedPolygonStatus !== 'idle' && (
              <div className="mt-2">
                {derivedPolygonStatus === 'fetching' && (
                  <div className="flex items-center gap-1.5 text-xs text-blue-600">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />Fetching boundary polygon…
                  </div>
                )}

                {derivedPolygonStatus === 'success' && selectedBoundary && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-800 space-y-0.5">
                    <p className="flex items-center gap-1.5 font-medium">
                      <CheckCircle className="h-3.5 w-3.5 shrink-0" />Department boundary polygon loaded
                    </p>
                    <p>
                      <span className="font-medium">Geometry:</span>{' '}
                      {selectedBoundary.geometry?.type ?? 'unknown'}
                      {selectedBoundary.geometry?.type === 'MultiPolygon'
                        ? ` (${selectedBoundary.geometry.coordinates?.length ?? 0} parts)`
                        : ''}
                    </p>
                    <p>
                      <span className="font-medium">Approx. area:</span>{' '}
                      {Number.isFinite(selectedBoundary.area)
                        ? `${Math.round(selectedBoundary.area).toLocaleString()} sq mi`
                        : '—'}
                    </p>
                    <p>
                      <span className="font-medium">Center:</span>{' '}
                      {Array.isArray(selectedBoundary.centroid)
                        ? `${selectedBoundary.centroid[1].toFixed(4)}, ${selectedBoundary.centroid[0].toFixed(4)}`
                        : '—'}
                    </p>
                    {selectedBoundary.verifiedAt && (
                      <p>
                        <span className="font-medium">Retrieved:</span>{' '}
                        {new Date(selectedBoundary.verifiedAt).toLocaleString()}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={handleRetryBoundaryFetch}
                      className="mt-1 text-xs font-medium text-green-700 underline hover:text-green-900"
                    >
                      Re-fetch boundary
                    </button>
                  </div>
                )}

                {derivedPolygonStatus === 'no_polygon' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                    <p className="flex items-center gap-1.5 font-medium">
                      <AlertCircle className="h-3.5 w-3.5 shrink-0" />No boundary polygon available for this department
                    </p>
                    <p className="mt-0.5">
                      NERIS has no jurisdiction polygon on file for this department
                      {info.nerisBoundaryUrl ? '' : ', and the verified record did not include a boundary source'}.
                      You can continue without it, but the boundary-based parcel filters in
                      Initial Data Setup will stay unavailable.
                    </p>
                    <button
                      type="button"
                      onClick={handleRetryBoundaryFetch}
                      className="mt-1.5 text-xs font-medium text-amber-800 underline hover:text-amber-900"
                    >
                      Try fetching again
                    </button>
                  </div>
                )}

                {derivedPolygonStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">
                    <p className="flex items-center gap-1.5 font-medium">
                      <XCircle className="h-3.5 w-3.5 shrink-0" />Boundary polygon fetch failed
                    </p>
                    <p className="mt-0.5">You can continue without it, or retry the fetch.</p>
                    <button
                      type="button"
                      onClick={handleRetryBoundaryFetch}
                      className="mt-1.5 text-xs font-medium text-red-700 underline hover:text-red-900"
                    >
                      Retry boundary fetch
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Department Type *</label>
            <select
              value={info.departmentType || ''}
              onChange={(e) => updateInfo({ departmentType: e.target.value })}
              title="Department Type"
              className={`w-full px-3 py-2 border ${errCls('departmentInfo.departmentType')} rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
            >
              <option value="">Select type</option>
              {DEPT_TYPES.map((t) => <option key={t} value={t} className="capitalize">{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Classification *</label>
            <select
              value={info.departmentClassification || ''}
              onChange={(e) => updateInfo({ departmentClassification: e.target.value })}
              title="Classification"
              className={`w-full px-3 py-2 border ${errCls('departmentInfo.departmentClassification')} rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
            >
              <option value="">Select classification</option>
              {DEPT_CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Timezone *</label>
            <select
              value={info.timezone || ''}
              onChange={(e) => updateInfo({ timezone: e.target.value })}
              title="Timezone"
              className={`w-full px-3 py-2 border ${errCls('departmentInfo.timezone')} rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
            >
              <option value="">Select timezone</option>
              {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
        </div>
      </section>

      {/* Address */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Address</h3>
        <DepartmentAddressField
          value={addr as DepartmentAddress}
          onChange={(next) => updateAddr(next)}
          hasError={hasError('departmentInfo.address.zip')}
          showCounty={false}
        />
      </section>

      {/* Jurisdiction */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-1">Jurisdiction</h3>
        <p className="text-xs text-gray-500 mb-3">Used for regulatory mapping and NERIS reporting. Each level cascades from the previous selection.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
            <select value={j.country?.code || ''} onChange={(e) => selectLookup('country', countries, e.target.value)}
              title="Country"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
              <option value="">Select country</option>
              {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">State / Province</label>
            <select value={j.state?.code || ''} onChange={(e) => selectLookup('state', statesList, e.target.value)}
              disabled={!j.country}
              title="State / Province"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50 disabled:text-gray-400">
              <option value="">Select state</option>
              {statesList.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">County</label>
            <select value={j.county?.code || ''} onChange={(e) => selectLookup('county', counties, e.target.value)}
              disabled={!j.state}
              title="County"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50 disabled:text-gray-400">
              <option value="">Select county</option>
              {counties.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="wizard-city" className="block text-sm font-medium text-gray-700 mb-1">City <span className="text-xs text-gray-400 font-normal">(optional)</span></label>
            <input
              id="wizard-city"
              type="text"
              value={j.city || ''}
              onChange={(e) => updateJurisdiction({ city: e.target.value || undefined })}
              placeholder="e.g. Atwater"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
        </div>
        {j.country && j.state && j.county && (
          <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-xs text-green-800">
              <span className="font-medium">Jurisdiction:</span>{' '}
              {[j.city, j.county?.name, j.state?.name, j.country?.name].filter(Boolean).join(', ')}
            </p>
          </div>
        )}
      </section>

      {/* Branding */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Branding</h3>
        <p className="text-xs text-gray-500 mb-3">
          Optional header logos. The primary logo fills the left slot and the secondary logo the right slot when each is enabled on a report.
        </p>
        <div className="space-y-4 mb-4">
          <DepartmentLogoUploadField
            label="Primary logo (left)"
            value={brand.logo}
            onChange={(ref) => updateBrand({ logo: ref ?? undefined })}
          />
          <DepartmentLogoUploadField
            label="Secondary logo (right)"
            value={brand.secondLogo}
            onChange={(ref) => updateBrand({ secondLogo: ref ?? undefined })}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={brand.primaryColor || '#B22222'}
                onChange={(e) => updateBrand({ primaryColor: e.target.value })}
                title="Primary color picker"
                className="h-9 w-12 rounded border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                value={brand.primaryColor || '#B22222'}
                onChange={(e) => updateBrand({ primaryColor: e.target.value })}
                title="Primary color hex value"
                placeholder="#B22222"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Color</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={brand.secondaryColor || '#1C1C1C'}
                onChange={(e) => updateBrand({ secondaryColor: e.target.value })}
                title="Secondary color picker"
                className="h-9 w-12 rounded border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                value={brand.secondaryColor || '#1C1C1C'}
                onChange={(e) => updateBrand({ secondaryColor: e.target.value })}
                title="Secondary color hex value"
                placeholder="#1C1C1C"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Background Opacity</label>
            <input
              type="range"
              min="0.05"
              max="1"
              step="0.05"
              value={brand.backgroundOpacity ?? 0.15}
              onChange={(e) => updateBrand({ backgroundOpacity: parseFloat(e.target.value) })}
              title="Background opacity"
              className="w-full mt-2"
            />
            <span className="text-xs text-gray-500">{((brand.backgroundOpacity ?? 0.15) * 100).toFixed(0)}%</span>
          </div>
        </div>
      </section>

      {/* NERIS Verify Confirm Modal */}
      {nerisVerifyOpen && nerisVerifyResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setNerisVerifyOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">Confirm Department</h3>
              <button type="button" onClick={() => setNerisVerifyOpen(false)} title="Close" aria-label="Close" className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-5 py-5 space-y-1">
              <p className="text-sm text-gray-500 mb-3">Is this the department associated with NERIS ID <span className="font-mono font-medium text-gray-800">{info.nerisId}</span>?</p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-1">
                <p className="text-sm font-semibold text-gray-900">{nerisVerifyResult.name}</p>
                <div className="text-xs text-gray-500 flex items-center gap-3">
                  {nerisVerifyResult.city && <span>{nerisVerifyResult.city}</span>}
                  {nerisVerifyResult.state && <span>{nerisVerifyResult.state}</span>}
                  {nerisVerifyResult.zipCode && <span>{nerisVerifyResult.zipCode}</span>}
                </div>
                {nerisVerifyResult.fdid && <p className="text-xs text-gray-400">FDID: {nerisVerifyResult.fdid}</p>}
                {nerisVerifyResult.address && <p className="text-xs text-gray-400">{nerisVerifyResult.address}</p>}
              </div>
              <p className="text-xs text-gray-400 pt-1">Confirming will save this data to the department record and fetch the jurisdiction polygon.</p>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button type="button" onClick={() => setNerisVerifyOpen(false)}
                className="px-4 py-2 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button type="button" onClick={handleVerifyConfirm}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1.5">
                <CheckCircle className="h-4 w-4" />Confirm &amp; Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NERIS Lookup Modal */}
      {nerisLookupOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setNerisLookupOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 flex flex-col max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">Look Up NERIS Department</h3>
              <button type="button" onClick={() => setNerisLookupOpen(false)} title="Close" aria-label="Close" className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            {/* Search inputs */}
            <div className="px-5 py-4 border-b border-gray-100 space-y-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={nerisSearchQ}
                  onChange={(e) => setNerisSearchQ(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleNerisSearch()}
                  placeholder="Department name (e.g. Durham Fire)"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
                <input
                  type="text"
                  value={nerisSearchState}
                  onChange={(e) => setNerisSearchState(e.target.value.toUpperCase().slice(0, 2))}
                  placeholder="ST"
                  maxLength={2}
                  className="w-16 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={handleNerisSearch}
                  disabled={!nerisSearchQ.trim() || nerisSearching}
                  className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  {nerisSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  Search
                </button>
              </div>
              <p className="text-xs text-gray-400">Search uses fuzzy matching (contains). Enter department name and optionally a 2-letter state abbreviation (e.g. AZ, NC, TX)</p>
            </div>
            {/* Results */}
            <div className="flex-1 overflow-y-auto">
              {nerisNotConfigured && (
                <div className="px-5 py-10 text-center">
                  <AlertCircle className="h-8 w-8 text-red-400 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-red-700">NERIS API Not Configured</p>
                  <p className="text-xs text-gray-500 mt-1 max-w-xs mx-auto">
                    The live department search requires NERIS API credentials. Contact your system administrator to set them up.
                  </p>
                </div>
              )}
              {!nerisNotConfigured && nerisSearchError && (
                <div className="px-5 py-4 text-sm text-red-600">{nerisSearchError}</div>
              )}
              {!nerisNotConfigured && !nerisSearchError && nerisSearchResults.length === 0 && !nerisSearching && nerisSearchQ.trim() && (
                <div className="px-5 py-8 text-center text-sm text-gray-400">No departments found. Try a broader search.</div>
              )}
              {!nerisNotConfigured && !nerisSearchError && nerisSearchResults.length === 0 && !nerisSearching && !nerisSearchQ.trim() && (
                <div className="px-5 py-8 text-center text-sm text-gray-400">Type a department name above and click Search.</div>
              )}
              {nerisSearchResults.map((r) => (
                <button
                  key={r.nerisId}
                  type="button"
                  onClick={() => handleNerisLookupSelect(r)}
                  className="w-full text-left px-5 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-0 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">{r.name}</span>
                    <span className="text-xs font-mono text-blue-600 ml-2 shrink-0">{r.nerisId}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                    {r.city && <span>{r.city}</span>}
                    {r.state && <span>{r.state}</span>}
                    {r.fdid && <span className="text-gray-400">FDID: {r.fdid}</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// LegacyDeptPicker — searchable department dropdown
// ---------------------------------------------------------------------------

function LegacyDeptPicker({
  departments,
  selectedDeptId,
  onSelect,
}: {
  departments: LegacyDepartment[];
  selectedDeptId: number | null;
  onSelect: (dept: LegacyDepartment) => void;
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return departments;
    const q = search.toLowerCase();
    return departments.filter(
      (d) =>
        (d.name ?? '').toLowerCase().includes(q) ||
        (d.city ?? '').toLowerCase().includes(q) ||
        (d.shortname ?? '').toLowerCase().includes(q) ||
        String(d.dept_id).includes(q) ||
        String(d.TenantId ?? '').includes(q),
    );
  }, [departments, search]);

  const selectedDept = selectedDeptId != null
    ? departments.find((d) => (d.TenantId ?? d.dept_id) === selectedDeptId)
    : null;

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-xs font-medium text-blue-800 mb-1">
        Select Department ({departments.length} found)
      </label>

      {/* Selected value display / trigger */}
      {!open && selectedDept ? (
        <button
          type="button"
          onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
          className="w-full text-left px-3 py-2 border border-green-300 rounded-lg text-sm bg-green-50 hover:bg-green-100 transition-colors flex items-center justify-between"
        >
          <span>
            <span className="font-medium text-green-900">{selectedDept.name}</span>
            <span className="text-green-600 ml-2 text-xs">
              {selectedDept.city}{selectedDept.state ? `, ${selectedDept.state}` : ''}
              {' · ID: '}{selectedDept.dept_id}
              {selectedDept.TenantId != null && ` · Tenant: ${selectedDept.TenantId}`}
            </span>
          </span>
          <span className="text-xs text-green-500">Change</span>
        </button>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-400 pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Type to search departments…"
            className="w-full pl-9 pr-3 py-2 border border-blue-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      )}

      {/* Dropdown list */}
      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-60 overflow-auto bg-white border border-blue-200 rounded-lg shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-3 py-4 text-sm text-gray-400 text-center">No departments match your search</div>
          ) : (
            filtered.map((d: LegacyDepartment) => {
              const id = d.TenantId ?? d.dept_id;
              const isSelected = id === selectedDeptId;
              return (
                <button
                  key={d.dept_id}
                  type="button"
                  onClick={() => {
                    onSelect(d);
                    setOpen(false);
                    setSearch('');
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors border-b border-gray-50 last:border-0 ${
                    isSelected ? 'bg-blue-50 font-medium' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-gray-900 truncate">{d.name || '(unnamed)'}</span>
                    <span className="text-[10px] text-gray-400 ml-2 shrink-0">ID: {d.dept_id}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {[d.city, d.state].filter(Boolean).join(', ') || '—'}
                    {d.TenantId != null && <span className="ml-2 text-blue-500">Tenant: {d.TenantId}</span>}
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
